import React from 'react';
import type { To } from 'react-router-dom';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

import Truncate from '@zimpel/common/components/Truncate';
import type { IconType } from '../Icon';

const StyledAppLink = styled.a<{ $hasUnderline?: boolean, $hasBrightHover?: boolean, $hasPadding?: boolean, $bold?: boolean }>`
  cursor: pointer;
  color: inherit;
  text-decoration: ${({ $hasUnderline }) => ($hasUnderline ? 'underline' : 'none')};
  font-weight: ${({ $bold }) => ($bold ? 'bold' : 'normal')};
  ${({ $hasPadding }) => $hasPadding && css`
    padding: 20px;
  `}
  
  &:hover {
    color: ${({ $hasBrightHover }) => ($hasBrightHover ? 'var(--color-text-bright)' : 'var(--color-primary-element-hover)')};
    text-decoration: ${({ $bold }) => ($bold ? 'none' : 'underline')};
  }
`;

const DisabledLink = styled.div<{ $hasPadding?: boolean }>`
  opacity: 0.5;
  pointer-events: none;
  ${({ $hasPadding }) => $hasPadding && css`
    padding: 20px;
  `}
`;

type AppLinkProps = {
  icon?: IconType;
  isLoading?: boolean;
  disabled?: boolean;
  hasUnderline?: boolean;
  bold?: boolean;
  truncateAtWidth?: string;
  hasBrightHover?: boolean;
  to?: To,
} & Partial<React.AnchorHTMLAttributes<HTMLAnchorElement>>;

const AppLink: React.FC<AppLinkProps> = ({
  icon,
  children,
  isLoading = false,
  disabled = false,
  truncateAtWidth,
  hasUnderline = false,
  hasBrightHover = false,
  bold = false,
  to,
  onClick,
  ...props
}) => {
  const iconElement = icon
    ? React.createElement(icon, { fixedWidth: true, rightMargin: !!children, isLoading })
    : null;

  let link: JSX.Element | null;
  if (disabled || isLoading) {
    link = (
      <DisabledLink>
        {iconElement}
        {children}
      </DisabledLink>
    );
  } else if (!to) {
    link = (
      <StyledAppLink
        $hasUnderline={hasUnderline}
        $bold={bold}
        $hasBrightHover={hasBrightHover}
        href="#"
        onClick={
          onClick
            ? (event) => { onClick(event); event.preventDefault(); }
            : undefined
        }
        {...props}
      >
        {iconElement}
        {children}
      </StyledAppLink>
    );
  } else {
    link = (
      <StyledAppLink
        $hasUnderline={hasUnderline}
        $hasBrightHover={hasBrightHover}
        as={Link}
        to={to}
        $bold={bold}
        onClick={onClick}
        {...props}
      >
        {iconElement}
        {children}
      </StyledAppLink>
    );
  }

  if (truncateAtWidth) {
    return (
      <Truncate
        width={truncateAtWidth}
        tooltip={typeof children === 'string' ? children : undefined}
      >
        {link}
      </Truncate>
    );
  }

  return link;
};

const EmailLink: React.FC<AppLinkProps> = ({ children, ...rest }) => (
  <AppLink href={`mailto:${children}`} hasUnderline {...rest}>{children}</AppLink>
);

const ExternalLink: React.FC<AppLinkProps> = (
  { children, ...rest },
) => (
  <AppLink rel="noopener noreferrer" target="_blank" hasUnderline {...rest}>
    {children || rest.href}
  </AppLink>
);

export {
  AppLink,
  EmailLink,
  ExternalLink,
};
